import clsx from 'clsx';
import { AlertCircle, Inbox, Lock } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { errorMessage } from '@/lib/api/errors';
import { Button } from './Button';
import styles from './StateViews.module.css';

/**
 * Empty, error and permission states.
 *
 * These are first-class components because the legacy app's defining defect was
 * conflating them: a failed fetch rendered as an empty state, so the user was told
 * "no data" when the truth was "it broke". Empty and error must never look alike.
 *
 * Props are declared `| undefined` rather than plain optional so callers can forward a
 * possibly-undefined value directly under exactOptionalPropertyTypes.
 */

interface StateShellProps {
  readonly title: string;
  readonly message?: ReactNode | undefined;
  readonly icon?: LucideIcon | undefined;
  readonly action?: ReactNode | undefined;
  readonly tone: string | undefined;
  readonly role?: 'alert' | undefined;
}

function StateShell({ title, message, icon: Icon, action, tone, role }: StateShellProps) {
  return (
    <div className={clsx(styles.container, tone)} role={role}>
      {Icon && <Icon className={styles.icon} size={28} aria-hidden="true" />}
      <h3 className={styles.title}>{title}</h3>
      {message !== undefined && <p className={styles.message}>{message}</p>}
      {action !== undefined && <div className={styles.action}>{action}</div>}
    </div>
  );
}

/** Nothing matched — a legitimate, successful, empty result. */
export function EmptyState({
  title,
  message,
  icon = Inbox,
  action,
}: {
  readonly title: string;
  readonly message?: ReactNode | undefined;
  readonly icon?: LucideIcon | undefined;
  readonly action?: ReactNode | undefined;
}) {
  return (
    <StateShell title={title} message={message} icon={icon} action={action} tone={styles.empty} />
  );
}

/** The request failed. Always offers a retry. */
export function ErrorState({
  error,
  onRetry,
  title = 'Could not load this',
}: {
  readonly error: unknown;
  readonly onRetry?: (() => void) | undefined;
  readonly title?: string;
}) {
  return (
    <StateShell
      title={title}
      message={errorMessage(error)}
      icon={AlertCircle}
      tone={styles.error}
      role="alert"
      action={
        onRetry && (
          <Button variant="secondary" size="sm" onClick={onRetry}>
            Try again
          </Button>
        )
      }
    />
  );
}

/** Authenticated but not permitted. Rendered instead of a silent redirect. */
export function ForbiddenState({
  message = 'You do not have permission to view this.',
}: {
  readonly message?: string;
}) {
  return <StateShell title="Access denied" message={message} icon={Lock} tone={styles.forbidden} />;
}
